'use client';

import { c as _c } from "react/compiler-runtime";
import { trackEvent } from '@svenskaspelab/nova-core/nextjs/analytics';
import { useCouponSettings } from '@svenskaspelab/poolbetting/core/frameworks/nextjs/coupon';
import { getValidatedQueryParameters } from '@svenskaspelab/poolbetting/core/lib/client';
import { COUPON_SETTINGS_SHEET, type SheetTypes } from '@svenskaspelab/poolbetting/schema/input';
import type { SettingsKeys } from '@svenskaspelab/poolbetting/schema/sessionstore';
import { TipsenSheet } from '@svenskaspelab/poolbetting/ui/TipsenSheet';
import { useSearchParams } from 'next/navigation';
import { type ReactNode, useCallback } from 'react';
import { useSheetState } from '../hooks/useSheetState';
const DEFAULT_SHEET_TITLE = 'Inställningar';
const DEFAULT_SHEET_DESCRIPTION = 'Ändra dina inställningar för kupongen här.';
type SheetClientWrapperProps = {
  /**
   * Valid sheet types for this page
   */
  validSheets: SheetTypes[];
  settingsKey: SettingsKeys;
  couponSettingComponent: ReactNode;
  quickPickComponent: ReactNode;
};
const oldTipsinfoOptions = {
  Odds: 'Odds',
  SvenskaFolket: 'Svenska folket',
  StartOdds: 'Startodds',
  Matchstart: 'Matchstart',
  FavoriteOdds: 'Favoritskap utifrån odds',
  Mutuals: 'Inbördes möten'
};

/**
 * Client wrapper for sheet page that opens the sheet immediately without waiting for server content.
 * Uses useSearchParams to read the sheet query parameter instantly and shows skeleton loading
 * while the server-rendered children stream in.
 *
 * @param root0 Component props
 * @param root0.validSheets Valid sheet types for this page
 * @param root0.couponSettingComponent Component for coupon settings sheet
 * @param root0.quickPickComponent Component for quick pick sheet
 * @param root0.settingsKey Key for storing settings in session store
 * @returns Sheet page component with immediate opening and skeleton loading
 */
export default function SheetClientWrapper(t0) {
  const $ = _c(14);
  const {
    validSheets,
    couponSettingComponent,
    quickPickComponent,
    settingsKey
  } = t0;
  const couponSettings = useCouponSettings("Powerplay", settingsKey);
  const searchParams = useSearchParams();
  let t1;
  if ($[0] !== searchParams) {
    t1 = getValidatedQueryParameters("sheet")(searchParams);
    $[0] = searchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    sheet: sheetFromQuery
  } = t1;
  const {
    activeSheet,
    isSheetLocked
  } = useSheetState(sheetFromQuery, validSheets);
  let title = DEFAULT_SHEET_TITLE;
  let description = DEFAULT_SHEET_DESCRIPTION;
  if (activeSheet) {
    bb0: switch (activeSheet) {
      case "couponSettings":
        {
          title = "Inst\xE4llningar";
          description = "\xC4ndra dina inst\xE4llningar f\xF6r kupongen h\xE4r.";
          break bb0;
        }
      case "quickPick":
        {
          title = "Fyll i med PIX!";
          description = "V\xE4lj hur du vill fylla i dina rader med PIX.";
          break bb0;
        }
      default:
        {
          title = DEFAULT_SHEET_TITLE;
          description = DEFAULT_SHEET_DESCRIPTION;
        }
    }
  }
  let content;
  bb1: switch (activeSheet) {
    case "couponSettings":
      {
        content = couponSettingComponent;
        break bb1;
      }
    case "quickPick":
      {
        content = quickPickComponent;
        break bb1;
      }
    default:
      {
        content = undefined;
      }
  }
  let t2;
  if ($[2] !== activeSheet || $[3] !== couponSettings || $[4] !== settingsKey) {
    t2 = isOpen => {
      if (activeSheet === COUPON_SETTINGS_SHEET && !isOpen && settingsKey === "PLAY") {
        trackEvent({
          category: "Nya Tipsen - Inst\xE4llningar",
          action: "tipsinfo",
          optLabel: couponSettings.data.tipsinfo.map(_temp).join(", ")
        });
      }
    };
    $[2] = activeSheet;
    $[3] = couponSettings;
    $[4] = settingsKey;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const onOpenChange = t2;
  let t3;
  if ($[6] !== activeSheet || $[7] !== content || $[8] !== description || $[9] !== isSheetLocked || $[10] !== onOpenChange || $[11] !== title || $[12] !== validSheets) {
    t3 = <TipsenSheet sheet={activeSheet} validSheets={validSheets} isLocked={isSheetLocked} title={title} onOpenChange={onOpenChange} description={description}>{content}</TipsenSheet>;
    $[6] = activeSheet;
    $[7] = content;
    $[8] = description;
    $[9] = isSheetLocked;
    $[10] = onOpenChange;
    $[11] = title;
    $[12] = validSheets;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  return t3;
}
function _temp(option) {
  return oldTipsinfoOptions[option];
}